import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import globeIcon from '@/features/Company/icons/globe-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import { video } from './index.css'
import titleLogo from './logo.jpg'

type Props = {
  metadata?: {
    __typename: string
    page: number
    page_size: number
    total_count: number
  }
  footerOnScreen: boolean
  listingsOnScreen: boolean
}

const DistrictRealtyProfile = ({ metadata }: Props) => {
  const { t } = useTranslation('districtrealty')
  const profile = {
    title: t('districtrealty:District Realty'),
    url: '/company/districtrealty'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <video autoPlay loop muted playsInline className={video}>
            <source
              src="/videos/districtrealty-banner-mobile.mp4"
              type="video/mp4"
              media="(max-width: 599px)"
            />
            <source
              src="/videos/districtrealty-banner.mp4"
              type="video/mp4"
              media="(min-width: 600px)"
            />
          </video>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={60}
                height={39}
                alt="District Realty"
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('districtrealty:heading')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t(`districtrealty:subheading1`)}
                  </Text>
                  <Text size="standard">{t('districtrealty:subheading2')}</Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`tel: 1-613-759-8383`}
                    rel="noreferrer noopener"
                  >
                    1-613-759-8383
                  </TextLink>
                </Box>

                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.districtrealty.com/ottawa-apartments/for-rent/"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.districtrealty.com
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('districtrealty:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default DistrictRealtyProfile
